<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { animeAPI } from '../lib/api';
  import Loading from '../components/Loading.svelte';

  export let id;
  export let location;

  let anime = null;
  let episodes = [];
  let currentEpisode = null;
  let loading = true;

  $: episodeNum = parseInt(new URLSearchParams(location.search).get('ep')) || 1;

  onMount(async () => {
    await loadData();
  });

  $: if (episodeNum) {
    currentEpisode = episodes.find(ep => ep.episodeNum === episodeNum);
  }

  async function loadData() {
    try {
      if (!id) {
        throw new Error('No anime ID provided');
      }

      const [animeRes, episodesRes] = await Promise.all([
        animeAPI.getById(id),
        animeAPI.getEpisodes(id)
      ]);
      
      anime = animeRes.data?.data || animeRes.data || null;
      episodes = episodesRes.data?.data || episodesRes.data || [];
      
      // Ensure episodes is an array
      if (!Array.isArray(episodes)) {
        console.warn('Unexpected episodes format:', episodes);
        episodes = [];
      }
      
      currentEpisode = episodes.find(ep => ep.episodeNum === episodeNum);
      
      if (!currentEpisode && episodes.length > 0) {
        console.warn(`Episode ${episodeNum} not found, showing first episode`);
        currentEpisode = episodes[0];
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      anime = null;
      episodes = [];
      currentEpisode = null;
    } finally {
      loading = false;
    }
  }

  function goToEpisode(epNum) {
    window.location.href = `/v/${id}?ep=${epNum}`;
  }
</script>

{#if loading}
  <Loading />
{:else if anime && currentEpisode}
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Breadcrumb -->
      <div class="mb-4 text-sm text-gray-400">
        <Link to="/home" class="hover:text-primary-600">Home</Link>
        <span class="mx-2">/</span>
        <Link to="/anime/{anime.id}" class="hover:text-primary-600">{anime.title}</Link>
        <span class="mx-2">/</span>
        <span>Episode {currentEpisode.episodeNum}</span>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Video Player -->
        <div class="lg:col-span-2">
          <div class="card">
            <div class="aspect-video bg-black">
              <iframe
                src={currentEpisode.videoUrl}
                title="Episode {currentEpisode.episodeNum}"
                class="w-full h-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            
            <div class="p-6">
              <h1 class="text-2xl font-bold mb-2">
                {anime.title} - Episode {currentEpisode.episodeNum}
              </h1>
              <p class="text-gray-400 mb-4">{currentEpisode.title}</p>
              
              <!-- Episode Navigation -->
              <div class="flex gap-4">
                {#if episodeNum > 1}
                  <button
                    on:click={() => goToEpisode(episodeNum - 1)}
                    class="btn bg-gray-800 hover:bg-gray-700 text-white flex-1"
                  >
                    ← Previous
                  </button>
                {/if}
                {#if episodeNum < episodes.length}
                  <button
                    on:click={() => goToEpisode(episodeNum + 1)}
                    class="btn btn-primary flex-1"
                  >
                    Next →
                  </button>
                {/if}
              </div>
            </div>
          </div>

          <!-- Anime Info -->
          <div class="card mt-6 p-6">
            <h2 class="text-xl font-bold mb-4">About {anime.title}</h2>
            <p class="text-gray-300 mb-4">{anime.description}</p>
            <div class="flex flex-wrap gap-2">
              {#each anime.genres as genre}
                <span class="px-3 py-1 bg-gray-800 rounded-full text-sm">{genre}</span>
              {/each}
            </div>
          </div>
        </div>

        <!-- Episodes List -->
        <div class="lg:col-span-1">
          <div class="card p-6 sticky top-20">
            <h2 class="text-xl font-bold mb-4">Episodes ({episodes.length})</h2>
            <div class="space-y-2 max-h-[600px] overflow-y-auto">
              {#each episodes as episode}
                <button
                  on:click={() => goToEpisode(episode.episodeNum)}
                  class="w-full text-left p-3 rounded-lg transition-colors {episode.episodeNum === episodeNum ? 'bg-primary-600 text-white' : 'bg-gray-800 hover:bg-gray-700'}"
                >
                  <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-16 h-10 rounded overflow-hidden">
                      <img 
                        src={episode.thumbnail} 
                        alt="Ep {episode.episodeNum}"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="font-semibold text-sm">Episode {episode.episodeNum}</p>
                      <p class="text-xs opacity-75 truncate">{episode.title}</p>
                    </div>
                  </div>
                </button>
              {/each}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{:else}
  <div class="min-h-screen flex items-center justify-center">
    <p class="text-2xl text-gray-400">Episode not found</p>
  </div>
{/if}
